"use client";
// Lado del navegador de las passkeys: pedir el reto al servidor, pedirle al dispositivo que firme (huella, cara o PIN) y mandar la respuesta.
import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, platformAuthenticatorIsAvailable, startAuthentication, startRegistration, WebAuthnAbortService } from "@simplewebauthn/browser";
import { guardarSesionProveedor } from "@/panel/portal-proveedor";

const post = async (ruta: string, cuerpo: object = {}) => {
  const r = await fetch(ruta, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(cuerpo), cache: "no-store" });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.mensaje ?? "No se pudo completar. Inténtalo de nuevo.");
  return d;
};

/** Error del dispositivo en palabras: cancelar no es un error que haya que gritar. */
export class Cancelada extends Error {}
function traducir(e: unknown): Error {
  const nombre = (e as { name?: string })?.name, causa = (e as { cause?: { name?: string } })?.cause?.name, codigo = (e as { code?: string })?.code;
  if (codigo === "ERROR_CEREMONY_ABORTED") return new Cancelada("Se canceló.");
  if (codigo === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") return new Error("Este dispositivo ya tiene una passkey guardada para tu cuenta.");
  if (codigo === "ERROR_INVALID_DOMAIN" || codigo === "ERROR_INVALID_RP_ID") return new Error("Las passkeys solo funcionan en la dirección segura del sitio.");
  if (nombre === "NotAllowedError" || causa === "NotAllowedError" || nombre === "AbortError" || causa === "AbortError") return new Cancelada("Se canceló.");
  if (nombre === "InvalidStateError" || causa === "InvalidStateError") return new Error("Este dispositivo ya tiene una passkey guardada para tu cuenta.");
  if (nombre === "SecurityError" || causa === "SecurityError") return new Error("Las passkeys solo funcionan en la dirección segura del sitio.");
  return e instanceof Error ? e : new Error("No se pudo completar. Inténtalo de nuevo.");
}

export const soportaPasskeys = () => typeof window !== "undefined" && browserSupportsWebAuthn();
export const tieneLectorPropio = () => platformAuthenticatorIsAvailable().catch(() => false);
export const soportaAutollenado = () => browserSupportsWebAuthnAutofill().catch(() => false);
export const cancelarPasskey = () => WebAuthnAbortService.cancelCeremony();

/** Entrar con passkey. Con `autollenado`, espera en silencio a que la persona elija su passkey en las sugerencias del campo de usuario. Devuelve el espacio. */
export async function entrarConPasskey(autollenado = false): Promise<"sistema" | "proveedor"> {
  const opciones = await post("/api/acceso/passkey-opciones");
  let respuesta;
  try { respuesta = await startAuthentication({ optionsJSON: opciones, useBrowserAutofill: autollenado }); } catch (e) { throw traducir(e); }
  const d = await post("/api/acceso/passkey-entrar", respuesta);
  if (d.espacio === "proveedor") guardarSesionProveedor({ token: "", id: d.id, nombre: d.nombre, tipo: d.tipo }); // el token quedó en la cookie protegida
  return d.espacio;
}

/** Nombre de inicio para la passkey, según el equipo donde se guarda. La persona lo puede cambiar. */
export function nombreDeEquipo() {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Teléfono Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Computadora con Windows";
  return "Este dispositivo";
}

/** Guardar una passkey nueva para la cuenta con sesión. `api` es /api/acceso/passkeys o /api/proveedor/passkeys. */
export async function guardarPasskey(api: string, nombre = nombreDeEquipo()) {
  const opciones = await post(`${api}/opciones`);
  let respuesta;
  try { respuesta = await startRegistration({ optionsJSON: opciones }); } catch (e) { throw traducir(e); }
  return post(`${api}/registrar`, { respuesta, nombre });
}

// «Guardar acceso con passkey» se ofrece una sola vez por cuenta y por navegador.
const llaveOferta = (id: string) => `sierra_oferta_passkey:${id}`;
export const yaSeOfrecio = (id: string) => { try { return !!localStorage.getItem(llaveOferta(id)); } catch { return true; } };
export const marcarOfrecida = (id: string) => { try { localStorage.setItem(llaveOferta(id), String(Date.now())); } catch { /* sin almacenamiento */ } };
